import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { Modal, Alert, message, Button, Form, Spin } from 'antd';
import {
	addWarrantySupplierToStoreState,
	fetchSupplierFacilitiesInPrivateNetworkForSupplier,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import { compareStrings, getWarrantyStatus, nullSafeGet } from '../../utils/DataAccessUtils';
import SupplierSelectionWorkOrderSelect from '../supplier_selection_work_order_select';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import {
	transferWorkOrderForPrivateSupplier,
	transferWorkOrderForSupplier,
} from '../../thunks/work_orders_thunks';
import { isInternalTech } from '../../utils/AuthUtils';
import { invoicesClientRestCrudThunks } from '../../thunks/invoices_thunks';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';

export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';
const TRANSFER_WORK_ORDER_FORM_SUPPLIER_FACILITIES_IN_PRIVATE_NETWORK_AUTOCOMPLETE =
	'TRANSFER_WORK_ORDER_FORM_SUPPLIER_FACILITIES_IN_PRIVATE_NETWORK_AUTOCOMPLETE';

interface TransferWorkOrderProps {
	onCancel?: any;
	createErrors: any;
	updateErrors: any;
	currentUser: any;
	supplierFacilities: any;
	fetchMultipleSupplierFacilitiesInPrivateNetwork: any;
	fetchSupplierFacilitiesInPrivateNetwork: any;
	creating: boolean;
	updating: boolean;
	workOrder: any;
	fetchSupplierFacility: any;
	addWarrantySupplier: any;
	transferWorkOrder: any;
	transferWorkOrderForPrivateSupplier: any;
	history: any;
	isClientView: boolean;
	getInvoiceCount: any;
	buttonProps: any;
	additionalOnChangeMethod: any;
	getInvoices: any;
	getInvoicesCount: any;
}

const TransferWorkOrder: React.FC<TransferWorkOrderProps> = ({
	workOrder,
	buttonProps = { ghost: true, children: 'Transfer' },
	additionalOnChangeMethod = () => {},

	supplierFacilities,
	createErrors,
	updateErrors,
	creating,
	updating,
	history,
	currentUser,

	transferWorkOrder,
	transferWorkOrderForPrivateSupplier,
	fetchMultipleSupplierFacilitiesInPrivateNetwork,
	fetchSupplierFacilitiesInPrivateNetwork,
	addWarrantySupplier,
	getInvoices,
	getInvoicesCount,
}) => {
	const { id: workOrderId, asset } = workOrder;
	const [visible, setVisible] = useState(false);
	const [hasUndisputedInvoice, setHasUndisputedInvoice] = useState(false);
	const [loading, setLoading] = useState(true);
	const [submitLoading, setSubmitLoading] = useState(false);

	const isPrivateSupplier = useMemo(() => isInternalTech(currentUser), [currentUser]);

	const checkInvoiceIsNotInvalidStatus = useCallback(() => {
		const disallowedStatuses = [
			INVOICE_STATUSES.pending,
			INVOICE_STATUSES.approved,
			INVOICE_STATUSES.processing,
			INVOICE_STATUSES.paid,
		];
		setLoading(true);
		getInvoicesCount({
			workOrderId,
			statuses: disallowedStatuses,
		}).then((n) => {
			if (n > 0) {
				setHasUndisputedInvoice(true);
			} else {
				setHasUndisputedInvoice(false);
			}
			setLoading(false);
		});
	}, [workOrder]);

	const [form] = Form.useForm();
	const okText = 'Transfer';
	const titleText = 'Transfer Work Order';
	const warranty = nullSafeGet('warranties.0', asset);

	const handleSubmit = async () => {
		form.validateFields().then((values) => {
			const methodToCall = isPrivateSupplier
				? transferWorkOrderForPrivateSupplier
				: transferWorkOrder;

			setSubmitLoading(true);
			return methodToCall(values)
				.then((wo) => {
					additionalOnChangeMethod(wo);
					message.success(`Work order transferred`);
					setVisible(false);

					if (!isPrivateSupplier) {
						history.push('/supplier/workOrders/overview/active');
					}
				})
				.catch((err) => {
					message.error(err || `Work order transfer failed.`);
				})
				.finally(() => {
					setSubmitLoading(false);
				});
		});
	};

	const makeVisible = useCallback(() => {
		setVisible(true);
		checkInvoiceIsNotInvalidStatus();
	}, [checkInvoiceIsNotInvalidStatus]);

	return (
		<>
			<Button {...buttonProps} onClick={makeVisible} />
			{visible ? (
				<Modal
					visible={visible}
					width={650}
					title={titleText}
					okText={okText}
					onCancel={() => setVisible(false)}
					onOk={handleSubmit}
					closable={false}
					confirmLoading={creating || updating}
					okButtonProps={{ ['disabled']: hasUndisputedInvoice, loading: submitLoading || loading }}
					footer={loading ? null : undefined}
					bodyStyle={{ padding: 0 }}
				>
					{loading ? (
						<span style={{ display: 'flex', justifyContent: 'center' }}>
							<Spin size="large" />
						</span>
					) : (
						<Form
							form={form}
							layout="vertical"
							className="supplierForm"
							initialValues={{
								id: nullSafeGet('id', workOrder),
								supplierFacilityId: nullSafeGet('supplierFacilityId', workOrder),
							}}
						>
							{createErrors.length > 0 && (
								<Form.Item>
									<Alert message={createErrors.join(' ')} type="error" />
								</Form.Item>
							)}
							{updateErrors.length > 0 && (
								<Form.Item>
									<Alert message={updateErrors.join(' ')} type="error" />
								</Form.Item>
							)}
							<Form.Item name="id" label="Work Order Id" hidden={true} />
							{hasUndisputedInvoice ? (
								<Alert
									type={'error'}
									message={
										<span
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											You cannot transfer suppliers while there is an undisputed invoice.
											<Button
												type={'link'}
												style={{ padding: 0 }}
												onClick={() => {
													setVisible(false);
													history.push(`/supplier/workOrders/detail/${workOrderId}/invoices`);
												}}
											>
												View Invoice <i className="icons8-font icons8-external-link ml-2" />
											</Button>
										</span>
									}
									style={{ marginBottom: 12 }}
								/>
							) : null}
							<Form.Item
								name="supplierFacilityId"
								label="Supplier"
								rules={[{ required: true, message: 'Please select a supplier' }]}
								style={{ padding: 24 }}
							>
								<SupplierSelectionWorkOrderSelect
									disabled={hasUndisputedInvoice}
									loading={loading}
									allowClear
									stateSlice={supplierFacilities}
									targetCollectionName={
										TRANSFER_WORK_ORDER_FORM_SUPPLIER_FACILITIES_IN_PRIVATE_NETWORK_AUTOCOMPLETE
									}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleSupplierFacilitiesInPrivateNetwork(ids, targetCollectionName);
									}}
									warrantySupplierId={nullSafeGet('provider.supplierFacilityId', warranty)}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) =>
										new Promise((resolve) =>
											fetchSupplierFacilitiesInPrivateNetwork(
												{ name: searchText, offset: 0, limit: 1000 },
												targetCollectionName,
												pagination,
												sorting,
												filters,
												addToTargetCollection
											).then((data) => {
												const warrantySupplierId = nullSafeGet(
													'warranties.0.provider.supplierFacilityId',
													asset
												);
												if (!data.some((supplier) => supplier.id === warrantySupplierId)) {
													resolve(
														addWarrantySupplier(
															warrantySupplierId,
															TRANSFER_WORK_ORDER_FORM_SUPPLIER_FACILITIES_IN_PRIVATE_NETWORK_AUTOCOMPLETE
														)
													);
												} else {
													resolve(data);
												}
											})
										)
									}
									renderRecord={(supplierFacility, apiSuppliers) => (
										<div>
											<Ellipsis
												tooltip
												length={32}
												fullWidthRecognition
												style={{ wordBreak: 'normal' }}
											>
												{supplierFacility.name}
											</Ellipsis>
											{asset &&
											getWarrantyStatus(nullSafeGet('warranties.0', asset)) === 'Active' &&
											nullSafeGet('warranties.0.provider.supplierFacilityId', asset) ===
												supplierFacility.id ? (
												<span className="mt-0 inline-block text-sm text-gray-400">(Warranty)</span>
											) : (
												<p className={'mb-0'}>
													{apiSuppliers.find((item) => item.id === supplierFacility.id) && (
														<span className="mt-0 inline-block text-sm text-gray-400">
															(Rank{' '}
															{apiSuppliers.findIndex((item) => supplierFacility.id === item.id) +
																1}
															)
														</span>
													)}
												</p>
											)}
										</div>
									)}
									sortBy={{}}
									additionalFilters={{
										problemTypeId: nullSafeGet('problemTypeId', workOrder),
										locationId: nullSafeGet('locationId', workOrder),
									}}
								/>
							</Form.Item>
						</Form>
					)}
				</Modal>
			) : null}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	supplierFacilities: state.supplier_facilities,
	currentUser: state.session.currentUser,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	history: ownProps.history,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch) => ({
	fetchSupplierFacility: (id) => dispatch(supplierFacilitiesRestCrudThunksForSupplier.readOne(id)),
	fetchMultipleSupplierFacilitiesInPrivateNetwork: (ids, targetCollectionName) =>
		dispatch(supplierFacilitiesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchSupplierFacilitiesInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters
	) =>
		dispatch(
			fetchSupplierFacilitiesInPrivateNetworkForSupplier(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		),
	addWarrantySupplier: (supplierId, targetCollectionName) =>
		dispatch(addWarrantySupplierToStoreState(supplierId, targetCollectionName)),
	transferWorkOrder: (entity) => dispatch(transferWorkOrderForSupplier(entity, 'id')),
	transferWorkOrderForPrivateSupplier: (entity) =>
		dispatch(transferWorkOrderForPrivateSupplier(entity, 'id')),
	getInvoices: (params, targetCollectionName) =>
		dispatch(invoicesClientRestCrudThunks.read(params, targetCollectionName)),
	getInvoicesCount: (params, targetCollectionName) =>
		dispatch(invoicesClientRestCrudThunks.countBy(params, targetCollectionName)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TransferWorkOrder));
